import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { CategoryService } from '../../handling_data/services/CategoryService';
import { CreateCategoryDto } from '../../handling_data/dtos/CategoryDto';


interface CategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    category?: any; // If present, it's edit mode
}

export const CategoryModal: React.FC<CategoryModalProps> = ({ isOpen, onClose, onSuccess, category }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<Omit<CreateCategoryDto, 'slug'>>({
        ar: { name: '' },
        en: { name: '' },
        tr: { name: '' }
    });

    useEffect(() => {
        if (category) {
            // Pre-fill form if editing
            setFormData({
                ar: { name: category.ar?.name || '' },
                en: { name: category.en?.name || '' },
                tr: { name: category.tr?.name || '' },
            });
        } else {
            // Reset form if adding
            setFormData({
                ar: { name: '' },
                en: { name: '' },
                tr: { name: '' }
            });
        }
    }, [category, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            if (category) {
                await CategoryService.updateCategory(category.id, formData);
            } else {
                await CategoryService.createCategory(formData);
            }
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to save category:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={category ? t('categories_page.edit_category') || 'تعديل التصنيف' : t('categories_page.add_new')}
        >
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tabs for Languages could be added here for better UI, but for now we list them */}
                <div className="space-y-4">
                    {['ar', 'en', 'tr'].map((lang) => (
                        <div key={lang} className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
                            <h4 className="text-sm font-bold text-primary flex items-center gap-2 uppercase">
                                <span className="w-2 h-2 rounded-full bg-primary"></span>
                                {lang === 'ar' ? 'العربية' : lang === 'en' ? 'English' : 'Türkçe'}
                            </h4>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('categories_page.table.name')}</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary/20 transition-all font-medium"
                                        value={formData[lang as 'ar' | 'en' | 'tr']?.name || ''}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            [lang]: { name: e.target.value }
                                        })}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-3 pt-4">
                    <Button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-primary text-white py-3 rounded-xl font-bold shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
                    >
                        {loading && <span className="w-5 h-5 animate-spin">⌛</span>}
                        {category ? t('common.save') : t('common.save')}
                    </Button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition-all"
                    >
                        {t('common.back')}
                    </button>
                </div>
            </form>
        </Modal>
    );
};
